import { fetchApi } from './api';
import type {
    InventoryItem,
    InventoryItemDetail,
    InventoryFilterOptions,
    InventoryComment,
    InventoryFilters,
    InventoryStats,
    InventoryFlagStats,
    FlaggedItem,
    FlagHistoryEntry,
    InventorySortField,
    InventoryTransactionsResponse,
    InventoryBuyersResponse,
    OpenOrdersResponse,
    StockHistoryResponse,
    MovementHistoryResponse,
    CrossCompanyResponse,
    EmailLogResponse,
    SortOrder,
} from '../types/inventory';

interface PaginatedResponse<T> {
    items: T[];
    total: number;
}

interface ListParams {
    page: number;
    limit: number;
    sortBy: InventorySortField;
    sortOrder: SortOrder;
    filters: InventoryFilters;
}

export const inventoryApi = {
    async list(params: ListParams): Promise<PaginatedResponse<InventoryItem>> {
        const searchParams = new URLSearchParams();
        searchParams.set('page', String(params.page));
        searchParams.set('limit', String(params.limit));
        searchParams.set('sortBy', params.sortBy);
        searchParams.set('sortOrder', params.sortOrder);

        const { filters } = params;
        if (filters.companyCode) searchParams.set('companyCode', filters.companyCode);
        if (filters.inventoryClass) searchParams.set('inventoryClass', filters.inventoryClass);
        if (filters.classGroup) searchParams.set('classGroup', filters.classGroup);
        if (filters.warehouseCountry) searchParams.set('warehouseCountry', filters.warehouseCountry);
        if (filters.warehouseCode) searchParams.set('warehouseCode', filters.warehouseCode);
        if (filters.supplierCode) searchParams.set('supplierCode', filters.supplierCode);
        if (filters.search) searchParams.set('search', filters.search);
        if (filters.itemId) searchParams.set('itemId', filters.itemId);
        if (filters.businessUnit) searchParams.set('businessUnit', filters.businessUnit);
        if (filters.parentCategory) searchParams.set('parentCategory', filters.parentCategory);
        if (filters.category) searchParams.set('category', filters.category);
        if (filters.onlyNewProducts) searchParams.set('onlyNewProducts', filters.onlyNewProducts);
        if (filters.onlyToBeDestroyed) searchParams.set('onlyToBeDestroyed', filters.onlyToBeDestroyed);
        if (filters.onlyMyCustomers) searchParams.set('onlyMyCustomers', filters.onlyMyCustomers);
        if (filters.salesManagerId) searchParams.set('salesManagerId', filters.salesManagerId);

        const response = await fetch(`/api/v1/inventory?${searchParams}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();

        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }

        return {
            items: body.data as InventoryItem[],
            total: body.pagination?.total ?? 0,
        };
    },

    get(itemId: string): Promise<InventoryItemDetail> {
        return fetchApi<InventoryItemDetail>(`/inventory/${encodeURIComponent(itemId)}`);
    },

    getFilters(currentFilters?: InventoryFilters): Promise<InventoryFilterOptions> {
        const params = new URLSearchParams();
        if (currentFilters?.companyCode) params.set('companyCode', currentFilters.companyCode);
        if (currentFilters?.inventoryClass) params.set('inventoryClass', currentFilters.inventoryClass);
        if (currentFilters?.classGroup) params.set('classGroup', currentFilters.classGroup);
        if (currentFilters?.businessUnit) params.set('businessUnit', currentFilters.businessUnit);
        if (currentFilters?.parentCategory) params.set('parentCategory', currentFilters.parentCategory);
        if (currentFilters?.warehouseCountry) params.set('warehouseCountry', currentFilters.warehouseCountry);
        if (currentFilters?.supplierCode) params.set('supplierCode', currentFilters.supplierCode);
        const qs = params.toString();
        return fetchApi<InventoryFilterOptions>(`/inventory-filters${qs ? `?${qs}` : ''}`);
    },

    getStats(currentFilters?: InventoryFilters): Promise<InventoryStats> {
        const params = new URLSearchParams();
        if (currentFilters?.companyCode) params.set('companyCode', currentFilters.companyCode);
        if (currentFilters?.inventoryClass) params.set('inventoryClass', currentFilters.inventoryClass);
        if (currentFilters?.classGroup) params.set('classGroup', currentFilters.classGroup);
        if (currentFilters?.warehouseCountry) params.set('warehouseCountry', currentFilters.warehouseCountry);
        if (currentFilters?.warehouseCode) params.set('warehouseCode', currentFilters.warehouseCode);
        if (currentFilters?.supplierCode) params.set('supplierCode', currentFilters.supplierCode);
        if (currentFilters?.search) params.set('search', currentFilters.search);
        if (currentFilters?.businessUnit) params.set('businessUnit', currentFilters.businessUnit);
        if (currentFilters?.parentCategory) params.set('parentCategory', currentFilters.parentCategory);
        if (currentFilters?.category) params.set('category', currentFilters.category);
        if (currentFilters?.onlyNewProducts) params.set('onlyNewProducts', currentFilters.onlyNewProducts);
        if (currentFilters?.onlyToBeDestroyed) params.set('onlyToBeDestroyed', currentFilters.onlyToBeDestroyed);
        if (currentFilters?.onlyMyCustomers) params.set('onlyMyCustomers', currentFilters.onlyMyCustomers);
        if (currentFilters?.salesManagerId) params.set('salesManagerId', currentFilters.salesManagerId);
        const qs = params.toString();
        return fetchApi<InventoryStats>(`/inventory-stats${qs ? `?${qs}` : ''}`);
    },

    addComment(
        itemId: string,
        commentText: string,
        commentType: string = 'note',
        lotCode?: string | null,
    ): Promise<InventoryComment> {
        return fetchApi<InventoryComment>(`/inventory/${encodeURIComponent(itemId)}/comments`, {
            method: 'POST',
            body: JSON.stringify({ commentText, commentType, lotCode: lotCode ?? null }),
        });
    },

    updateComment(itemId: string, commentId: number, commentText: string): Promise<unknown> {
        return fetchApi(`/inventory/${encodeURIComponent(itemId)}/comments/${commentId}`, {
            method: 'PUT',
            body: JSON.stringify({ commentText }),
        });
    },

    deleteComment(itemId: string, commentId: number): Promise<void> {
        return fetchApi<void>(`/inventory/${encodeURIComponent(itemId)}/comments/${commentId}`, {
            method: 'DELETE',
        });
    },

    getTransactions(itemId: string, lotCode?: string | null): Promise<InventoryTransactionsResponse> {
        const qs = lotCode ? `?lotCode=${encodeURIComponent(lotCode)}` : '';
        return fetchApi<InventoryTransactionsResponse>(`/inventory/${encodeURIComponent(itemId)}/transactions${qs}`);
    },

    async exportTransactions(itemId: string, lotCode?: string | null): Promise<void> {
        const qs = lotCode ? `?lotCode=${encodeURIComponent(lotCode)}` : '';
        const res = await fetch(`/api/v1/inventory/${encodeURIComponent(itemId)}/transactions/export${qs}`);
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        const blob = await res.blob();
        const cd = res.headers.get('Content-Disposition') ?? '';
        const m = /filename="([^"]+)"/.exec(cd);
        const filename = m?.[1] ?? `Transactions_${itemId}.xlsx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    },

    // Flag operations (lotCode required for Expired/QualityIssue, must be null for ToBeDestroyed/NewProduct)
    setFlag(
        itemId: string,
        flagType: string,
        reason?: string,
        lotCode?: string | null,
    ): Promise<{ id: number }> {
        return fetchApi<{ id: number }>(`/inventory/${encodeURIComponent(itemId)}/flags`, {
            method: 'POST',
            body: JSON.stringify({ flagType, reason, lotCode: lotCode ?? null }),
        });
    },

    removeFlag(
        itemId: string,
        flagType: string,
        reason?: string,
        lotCode?: string | null,
    ): Promise<{ removed: boolean }> {
        const params = new URLSearchParams();
        if (reason) params.set('reason', reason);
        if (lotCode) params.set('lotCode', lotCode);
        const qs = params.toString();
        return fetchApi<{ removed: boolean }>(
            `/inventory/${encodeURIComponent(itemId)}/flags/${flagType}${qs ? `?${qs}` : ''}`,
            { method: 'DELETE' },
        );
    },

    getFlagStats(currentFilters?: InventoryFilters): Promise<InventoryFlagStats> {
        const params = new URLSearchParams();
        if (currentFilters?.companyCode) params.set('companyCode', currentFilters.companyCode);
        if (currentFilters?.inventoryClass) params.set('inventoryClass', currentFilters.inventoryClass);
        if (currentFilters?.classGroup) params.set('classGroup', currentFilters.classGroup);
        if (currentFilters?.warehouseCountry) params.set('warehouseCountry', currentFilters.warehouseCountry);
        if (currentFilters?.warehouseCode) params.set('warehouseCode', currentFilters.warehouseCode);
        if (currentFilters?.supplierCode) params.set('supplierCode', currentFilters.supplierCode);
        if (currentFilters?.search) params.set('search', currentFilters.search);
        if (currentFilters?.businessUnit) params.set('businessUnit', currentFilters.businessUnit);
        if (currentFilters?.parentCategory) params.set('parentCategory', currentFilters.parentCategory);
        if (currentFilters?.category) params.set('category', currentFilters.category);
        const qs = params.toString();
        return fetchApi<InventoryFlagStats>(`/inventory-flag-stats${qs ? `?${qs}` : ''}`);
    },

    getFlaggedItems(flagType?: string): Promise<FlaggedItem[]> {
        const params = flagType ? `?flagType=${flagType}` : '';
        return fetchApi<FlaggedItem[]>(`/inventory-flags${params}`);
    },

    getBuyers(itemId: string): Promise<InventoryBuyersResponse> {
        return fetchApi<InventoryBuyersResponse>(`/inventory/${encodeURIComponent(itemId)}/buyers`);
    },

    getOpenOrders(itemId: string): Promise<OpenOrdersResponse> {
        return fetchApi<OpenOrdersResponse>(`/inventory/${encodeURIComponent(itemId)}/open-orders`);
    },

    getStockHistory(itemId: string): Promise<StockHistoryResponse> {
        return fetchApi<StockHistoryResponse>(`/inventory/${encodeURIComponent(itemId)}/stock-history`);
    },

    getMovementHistory(itemId: string): Promise<MovementHistoryResponse> {
        return fetchApi<MovementHistoryResponse>(`/inventory/${encodeURIComponent(itemId)}/movement-history`);
    },

    getCrossCompany(itemId: string): Promise<CrossCompanyResponse> {
        return fetchApi<CrossCompanyResponse>(`/inventory/${encodeURIComponent(itemId)}/cross-company`);
    },

    getEmailLog(itemId: string): Promise<EmailLogResponse> {
        return fetchApi<EmailLogResponse>(`/inventory/${encodeURIComponent(itemId)}/emails`);
    },

    getFlagHistory(itemId?: string, flagType?: string): Promise<FlagHistoryEntry[]> {
        const params = new URLSearchParams();
        if (itemId) params.set('itemId', itemId);
        if (flagType) params.set('flagType', flagType);
        const qs = params.toString();
        return fetchApi<FlagHistoryEntry[]>(`/inventory-flag-history${qs ? `?${qs}` : ''}`);
    },
};
